import type { ReactNode } from 'react';

type Props = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Visible next to the box; may be any node so callers can inline
   *  markdown / help bubbles / etc. When null the label slot collapses
   *  and the box is rendered on its own. */
  children?: ReactNode;
  /** Extra classes on the outer label — used for the CheckboxGroup case
   *  to keep top-alignment when the label wraps to multiple lines. */
  className?: string;
  /**
   * Accessibility wiring for the native input (WCAG 2.1 — 3.3.1, 4.1.2).
   * The accessible NAME still comes from `children` via the wrapping label;
   * these carry the state and the pointer to any error / hint text, which a
   * screen reader otherwise never reaches.
   */
  id?: string;
  ariaDescribedBy?: string;
  ariaRequired?: boolean;
  ariaInvalid?: boolean;
};

/**
 * BMO-styled checkbox — mirrors <BmoRadioGroup> but with a 24 px square
 * (2 px border, 4 px radius) instead of a circle, and a BMO-blue tick
 * when checked. Native checkbox lives visually hidden behind the styled
 * box so keyboard + assistive tech behavior stays intact.
 *
 * Standalone control (unlike the radio group). Callers stack instances
 * themselves — the CheckboxGroup case in FieldRenderer wraps the group
 * in its own flex column so the reveal slot lives under each option.
 */
export function BmoCheckbox({
  checked,
  onChange,
  disabled,
  children,
  className,
  id,
  ariaDescribedBy,
  ariaRequired,
  ariaInvalid,
}: Props) {
  return (
    <label className={`bmo-checkbox${className ? ` ${className}` : ''}`}>
      <input
        type="checkbox"
        id={id}
        checked={checked}
        disabled={disabled}
        aria-describedby={ariaDescribedBy}
        aria-required={ariaRequired || undefined}
        aria-invalid={ariaInvalid || undefined}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="bmo-checkbox__box">
        <span className="bmo-checkbox__tick" aria-hidden="true">
          {/* Simple SVG check so we don't inherit any AntD-icon width
              quirks or need to load the icon font just for a tick. */}
          <svg viewBox="0 0 14 14" width="14" height="14">
            <path
              d="M2.2 7.2 L5.6 10.6 L11.8 4.4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </span>
      {children !== undefined && children !== null && (
        <span className="bmo-checkbox__label">{children}</span>
      )}
    </label>
  );
}
